import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  setDoc,
  getDocs,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import useCoachesCatalog from '../hooks/useCoachesCatalog';
import {
  staticCoachesAsDocs,
  coachWritePayload,
  coachSlug,
  coachRoleLine,
} from '../utils/coachesCatalog';
import placeholder from '../assets/images/placeholder.jpg';
import './CoachesAdmin.css';

const emptyForm = () => ({
  name: '',
  role: '',
  tagline: '',
  bio: '',
  photoUrl: '',
  sortOrder: '',
});

export default function CoachesAdmin() {
  const { coaches, source, loading } = useCoachesCatalog();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return coaches;
    return coaches.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        String(c.role || '')
          .toLowerCase()
          .includes(q) ||
        String(c.tagline || '')
          .toLowerCase()
          .includes(q)
    );
  }, [coaches, filter]);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setPhotoFile(null);
    setPhotoPreview('');
  };

  const startEdit = (coach) => {
    setEditingId(coach.id);
    setForm({
      name: coach.name,
      role: coach.role || '',
      tagline: coach.tagline || '',
      bio: coach.bio || '',
      photoUrl: coach.photoUrl || '',
      sortOrder: coach.sortOrder === 0 || coach.sortOrder ? String(coach.sortOrder) : '',
    });
    setPhotoFile(null);
    setPhotoPreview('');
    setMessage({ type: '', text: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    const name = String(form.name || '').trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Coach name is required.' });
      return;
    }
    if (photoFile && !photoFile.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Photo must be an image.' });
      return;
    }
    if (photoFile && photoFile.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Photo must be 10 MB or smaller.' });
      return;
    }

    const existing = editingId ? coaches.find((c) => c.id === editingId) : null;
    const slug = existing?.coachSlug || coachSlug(name);
    const docId = existing?.id || slug;

    if (!existing && coaches.some((c) => c.id === docId)) {
      setMessage({
        type: 'error',
        text: 'A coach with that name already exists. Edit the existing coach instead.',
      });
      return;
    }

    setSaving(true);
    try {
      let photoUrl = form.photoUrl || '';
      if (photoFile) {
        const user = auth.currentUser;
        if (!user) throw new Error('Not signed in');
        const rawExt = (photoFile.name.split('.').pop() || 'jpg').toLowerCase();
        const ext = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(rawExt) ? rawExt : 'jpg';
        const path = `coachPhotos/${user.uid}/${slug}/${Date.now()}.${ext}`;
        const sref = ref(storage, path);
        await uploadBytes(sref, photoFile, { contentType: photoFile.type || 'image/jpeg' });
        photoUrl = await getDownloadURL(sref);
      }

      const payload = coachWritePayload(
        {
          ...form,
          name,
          photoUrl,
          sortOrder: form.sortOrder === '' ? coaches.length + 1 : Number(form.sortOrder),
          active: true,
        },
        { existingSlug: slug }
      );
      payload.updatedAt = Timestamp.now();
      payload.updatedBy = auth.currentUser?.uid || '';

      await setDoc(doc(db, 'coaches', docId), payload, { merge: true });
      setMessage({
        type: 'success',
        text: existing ? `Updated ${name}.` : `Added ${name} to the coaching staff.`,
      });
      resetForm();
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Could not save coach. Deploy latest Firestore/Storage rules if this is the first time using Team coaches.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coach) => {
    const ok = window.confirm(
      `Remove ${coach.name} from the coaching staff on the home page?`
    );
    if (!ok) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const slug = coach.coachSlug || coachSlug(coach.name);
      const payload = coachWritePayload(
        {
          name: coach.name,
          role: coach.role || '',
          tagline: coach.tagline || '',
          bio: coach.bio || '',
          photoUrl: typeof coach.photoUrl === 'string' ? coach.photoUrl : '',
          sortOrder: coach.sortOrder,
          active: false,
        },
        { existingSlug: slug }
      );
      payload.updatedAt = Timestamp.now();
      payload.updatedBy = auth.currentUser?.uid || '';
      await setDoc(doc(db, 'coaches', coach.id), payload, { merge: true });
      if (editingId === coach.id) resetForm();
      setMessage({ type: 'success', text: `Removed ${coach.name}.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Could not remove coach.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSeedStatic = async () => {
    const ok = window.confirm(
      source === 'static'
        ? 'Import the built-in coaching staff into Firestore so edits persist?'
        : 'Import any missing built-in coaches (will not overwrite existing docs)?'
    );
    if (!ok) return;

    setSeeding(true);
    setMessage({ type: '', text: '' });
    try {
      const seed = staticCoachesAsDocs();
      const existingSnap = await getDocs(collection(db, 'coaches'));
      const existingIds = new Set(existingSnap.docs.map((d) => d.id));
      let written = 0;
      let batch = writeBatch(db);
      let ops = 0;
      for (const c of seed) {
        if (existingIds.has(c.id)) continue;
        const payload = coachWritePayload(c, { existingSlug: c.coachSlug });
        if (c.photoUrl) payload.photoUrl = c.photoUrl;
        payload.updatedAt = Timestamp.now();
        payload.updatedBy = auth.currentUser?.uid || 'seed';
        batch.set(doc(db, 'coaches', c.id), payload, { merge: false });
        written += 1;
        ops += 1;
        if (ops >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) await batch.commit();
      setMessage({
        type: 'success',
        text:
          written === 0
            ? 'All built-in coaches are already in Firestore.'
            : `Imported ${written} coach${written === 1 ? '' : 'es'} into Firestore.`,
      });
    } catch (err) {
      console.error(err);
      setMessage({
        type: 'error',
        text: 'Import failed. Deploy Firestore rules for coaches, then try again.',
      });
    } finally {
      setSeeding(false);
    }
  };

  const previewSrc = photoPreview || form.photoUrl || placeholder;

  return (
    <div className="coaches-admin">
      <h2 className="admin-main-section-title">Team coaches</h2>
      <p className="coaches-admin-lead">
        Manage the Leadership Team on the home page: name, photo, role, tagline, and bio. Add, edit, or
        remove coaches anytime.
      </p>

      <div className="coaches-admin-toolbar">
        <div className="coaches-admin-source">
          {loading ? (
            <span>Loading coaches…</span>
          ) : source === 'firestore' ? (
            <span>
              Live source: <strong>Firestore</strong> ({coaches.length})
            </span>
          ) : source === 'hybrid' ? (
            <span>
              Live source: <strong>Firestore + built-in</strong> ({coaches.length})
            </span>
          ) : (
            <span>
              Live source: <strong>built-in list</strong> ({coaches.length})
            </span>
          )}
        </div>
        <button type="button" className="admin-button" disabled={seeding || saving} onClick={handleSeedStatic}>
          {seeding ? 'Importing…' : 'Import built-in coaches'}
        </button>
      </div>

      {message.text ? (
        <p className={`coaches-admin-message ${message.type === 'success' ? 'is-success' : 'is-error'}`}>
          {message.text}
        </p>
      ) : null}

      <form className="coaches-admin-form" onSubmit={handleSave}>
        <h3>{editingId ? 'Edit coach' : 'Add coach'}</h3>
        <div className="coaches-admin-form-grid">
          <label>
            Name
            <input
              className="admin-input"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              placeholder="Full name"
            />
          </label>
          <label>
            Role
            <input
              className="admin-input"
              value={form.role}
              onChange={(e) => updateField('role', e.target.value)}
              placeholder="e.g. Head Coach"
            />
          </label>
          <label>
            Tagline
            <input
              className="admin-input"
              value={form.tagline}
              onChange={(e) => updateField('tagline', e.target.value)}
              placeholder="e.g. Bench Boss"
            />
          </label>
          <label>
            Sort order
            <input
              className="admin-input"
              type="number"
              min="0"
              value={form.sortOrder}
              onChange={(e) => updateField('sortOrder', e.target.value)}
              placeholder="1"
            />
          </label>
        </div>

        <div className="coaches-admin-photo-row">
          <img src={previewSrc} alt="" className="coaches-admin-photo-preview" />
          <div>
            <p className="coaches-admin-hint">
              Square or portrait headshot works best (shown as a circle on the home page). JPEG/PNG, max 10 MB.
            </p>
            <label className="coaches-admin-file">
              <span className="admin-button">Choose photo</span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  setPhotoFile(e.target.files?.[0] || null);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        </div>

        <label className="coaches-admin-block">
          Bio
          <textarea
            className="admin-input"
            rows={4}
            value={form.bio}
            onChange={(e) => updateField('bio', e.target.value)}
            placeholder="Short bio shown on the home page"
          />
        </label>

        <div className="coaches-admin-form-actions">
          <button type="submit" className="admin-button" disabled={saving || seeding}>
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add coach'}
          </button>
          {editingId ? (
            <button type="button" className="admin-button" disabled={saving} onClick={resetForm}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </form>

      <div className="coaches-admin-list-header">
        <h3 className="coaches-admin-list-title">Current staff ({coaches.length})</h3>
        <input
          className="admin-input coaches-admin-filter"
          placeholder="Filter by name or role…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="coaches-admin-table-wrap">
        <table className="admin-table coaches-admin-table">
          <thead>
            <tr>
              <th className="coaches-admin-th-photo">Photo</th>
              <th className="coaches-admin-th-name">Name</th>
              <th className="coaches-admin-th-role">Role</th>
              <th className="coaches-admin-th-order">Order</th>
              <th className="coaches-admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ color: '#888' }}>
                  No coaches yet.
                </td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.id}>
                  <td className="coaches-admin-td-photo">
                    <img
                      src={c.photoUrl || placeholder}
                      alt=""
                      className="coaches-admin-thumb"
                    />
                  </td>
                  <td className="coaches-admin-td-name">
                    <span className="coaches-admin-player-name">{c.name}</span>
                    {c.bio ? (
                      <span className="coaches-admin-player-bio" title={c.bio}>
                        {c.bio}
                      </span>
                    ) : null}
                  </td>
                  <td className="coaches-admin-td-role">{coachRoleLine(c) || '—'}</td>
                  <td className="coaches-admin-td-order">{c.sortOrder || '—'}</td>
                  <td className="coaches-admin-td-actions">
                    <div className="coaches-admin-row-actions">
                      <button
                        type="button"
                        className="admin-button coaches-admin-action-btn"
                        onClick={() => startEdit(c)}
                        disabled={saving}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="admin-button-archive coaches-admin-action-btn"
                        onClick={() => handleDelete(c)}
                        disabled={saving}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
